/**
 * Focused per-work-order assembly workspace at
 * `/manufacturing/assembly/work-orders/:buildOrderId`.
 *
 * Full-width, single purpose: label print, serialized PCBA assembly, or TLA BOM review.
 * The queue is one route up (`/manufacturing/assembly`). Labor starts on this route only.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Printer } from "lucide-react";

import { WorkOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { ApiError } from "@/lib/api";
import { formatLaborLocationLabel } from "@/lib/labor/laborPageKey";
import { laborRemotePersistenceDisabled } from "@/lib/labor/laborEnv";
import { flashAndBumpToOverview } from "@/lib/labor/laborTimeoutHandlers";
import { useLaborDevStatusPanelRegistration } from "@/lib/labor/laborDevStatusPanelContext";
import type { LaborStatusPanelProps } from "@/lib/labor/LaborStatusPanel";
import { useLaborSession } from "@/lib/labor/useLaborSession";
import { ZPL_PRINTER_STORAGE_KEYS } from "@/lib/zplPrinter";
import { useZplPrintLocation } from "@/lib/zplPrintLocation";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";

import { effectiveWorkOrderReceivedQuantity } from "./workOrdersPageModel";
import { assemblyInstructionRevisionTitleSuffix } from "./assemblyListFormat";
import { AssemblyComponentSwapSection } from "./AssemblyComponentSwapSection";
import { toggleAssemblySwapSlotVariables } from "./assemblyComponentSwap";
import { AssemblyHubBomButton } from "./AssemblyHubBomButton";
import { AssemblyWorkInstructionStepsList } from "./AssemblyWorkInstructionStepsList";
import {
  ASSEMBLY_WORK_ORDERS_QUERY_KEY,
  recordAssemblyUnit,
  fetchAssemblyHubBom,
  fetchAssemblyUnits,
  fetchAssemblyWorkOrderDetail,
  printAssemblyLabel,
  type AssemblyWorkOrderDetail,
  type HubBomLine,
  type HubBomResponse,
} from "./assemblyApi";
import { assemblyLaborPageKeyForWorkOrder } from "./assemblyLaborPage";
import { ASSEMBLY_LABOR_FLASH_STORAGE_KEY } from "./assemblyLaborFlash";
import { assemblyQueuePath } from "./assemblyNavigation";

const WORK_PAGE_HELP =
  "Assembly work page for jobs at SGS: print magnet labels, scan PCBA serials to record assembled units, or review Megaventory TLA BOM lines. Labor clocks in when you open the work order.";

export function AssemblyWorkOrderPage(): JSX.Element {
  const params = useParams<{ buildOrderId: string }>();
  const buildOrderId = Number.parseInt(params.buildOrderId ?? "", 10);
  const validId = Number.isFinite(buildOrderId) && buildOrderId > 0;
  const navigate = useNavigate();

  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("manufacturing");
  const queryClient = useQueryClient();

  const [pcbaSerialDraft, setPcbaSerialDraft] = useState("");
  const [swappedVariableNames, setSwappedVariableNames] = useState<Set<string>>(() => new Set());
  const pcbaSerialInputRef = useRef<HTMLInputElement>(null);

  const laborPage = useMemo(
    () => (validId ? assemblyLaborPageKeyForWorkOrder(buildOrderId) : ""),
    [validId, buildOrderId],
  );

  const bumpAssemblyLaborToQueueWithFlash = useCallback(
    (message: string) => {
      flashAndBumpToOverview({
        navigate,
        to: assemblyQueuePath(),
        storageKey: ASSEMBLY_LABOR_FLASH_STORAGE_KEY,
        message,
        replace: true,
      });
    },
    [navigate],
  );

  const labor = useLaborSession({
    page: laborPage,
    domain: "manufacturing",
    moduleGate: "manufacturing",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled(),
    workLabel: "Assembly",
    autoStart: laborPage !== "",
    requirePersistedClockIn: true,
    onIdleTimeout: () =>
      bumpAssemblyLaborToQueueWithFlash(
        "Clocked out due to inactivity on this assembly work order. Open it again from the queue when you return.",
      ),
    onPersistedSessionLost: () =>
      bumpAssemblyLaborToQueueWithFlash(
        "Your hub labor session for this assembly work order ended. Use the Assembly breadcrumb to return to the queue, then open the work order again to clock back in.",
      ),
    onSessionEndedRemotely: () =>
      bumpAssemblyLaborToQueueWithFlash(
        "Clocked out from another tab or the session was closed on the server. Open the work order again from the queue when you want to continue.",
      ),
    onStopped: () =>
      bumpAssemblyLaborToQueueWithFlash(
        "Clocked out. Open the work order again from the queue when you want to continue.",
      ),
    localOnlyMessage: (reason) =>
      reason === "read-only"
        ? "Started working. Your role is read-only for Manufacturing, so this is a local timer only."
        : "Started working. Labor writes are blocked, so this is a local timer only.",
  });

  const laborGateReady = laborPage !== "" && labor.gateReady;

  const laborSnapshot = useMemo((): LaborStatusPanelProps => {
    return {
      isWorking: labor.isWorking,
      elapsedSeconds: labor.elapsedSeconds,
      workLabel: "Assembly",
      banner: labor.banner,
      lastSummary: labor.lastSummary,
      heartbeatIntervalMs: labor.heartbeatIntervalMs,
      idleTimeoutMs: labor.idleTimeoutMs,
    };
  }, [
    labor.isWorking,
    labor.elapsedSeconds,
    labor.banner,
    labor.lastSummary,
    labor.heartbeatIntervalMs,
    labor.idleTimeoutMs,
  ]);

  useLaborDevStatusPanelRegistration(import.meta.env.DEV && laborPage !== "", laborSnapshot);

  const laborGateConflictBumpRef = useRef(false);
  useEffect(() => {
    if (!validId) return;
    if (labor.loading) return;
    if (labor.activeOnOtherPage) {
      if (!laborGateConflictBumpRef.current) {
        laborGateConflictBumpRef.current = true;
        bumpAssemblyLaborToQueueWithFlash(
          `You are already clocked in on ${formatLaborLocationLabel(labor.activeOnOtherPage)}. Return to the queue, stop that session, then open this work order again.`,
        );
      }
      return;
    }
    laborGateConflictBumpRef.current = false;
  }, [validId, labor.loading, labor.activeOnOtherPage, bumpAssemblyLaborToQueueWithFlash]);

  const laborGateFailureBumpRef = useRef(false);
  useEffect(() => {
    if (!validId) return;
    if (labor.loading || labor.gateReady) return;
    if (labor.activeOnOtherPage) return;
    if (!labor.banner?.message) return;
    if (laborGateFailureBumpRef.current) return;
    laborGateFailureBumpRef.current = true;
    bumpAssemblyLaborToQueueWithFlash(labor.banner.message);
  }, [
    validId,
    labor.loading,
    labor.gateReady,
    labor.activeOnOtherPage,
    labor.banner?.message,
    bumpAssemblyLaborToQueueWithFlash,
  ]);

  useEffect(() => {
    if (labor.gateReady) {
      laborGateFailureBumpRef.current = false;
    }
  }, [labor.gateReady]);

  const { station, setStation, selectedPrinter: printer, unavailableMessage } = useZplPrintLocation({
    storageKey: ZPL_PRINTER_STORAGE_KEYS.assembly,
    labelSize: "4x2",
    dpi: 300,
  });
  const [labelCopies, setLabelCopies] = useState("1");

  const detailQuery = useQuery({
    queryKey: ["manufacturing", "assembly", "detail", buildOrderId],
    queryFn: () => fetchAssemblyWorkOrderDetail(buildOrderId),
    enabled: validId && laborGateReady,
    staleTime: 5_000,
  });

  const unitsQuery = useQuery({
    queryKey: ["manufacturing", "assembly", "units", buildOrderId],
    queryFn: () => fetchAssemblyUnits(buildOrderId),
    enabled: validId && laborGateReady && detailQuery.data?.assembly_type === "serialized",
    staleTime: 5_000,
  });

  const outputSku = detailQuery.data?.sku?.trim() ?? "";
  const hubBomSku =
    detailQuery.data?.assembly_type === "label" || detailQuery.data?.assembly_type === "serialized"
      ? outputSku
      : "";
  const assemblyKind = detailQuery.data?.assembly_kind ?? null;
  const hubBomQuery = useQuery({
    queryKey: ["manufacturing", "assembly", "hub-bom", hubBomSku, assemblyKind],
    queryFn: () => fetchAssemblyHubBom(hubBomSku, assemblyKind),
    enabled: hubBomSku.length > 0 && laborGateReady,
    staleTime: 60_000,
  });
  const hubBomMeta: Pick<HubBomResponse, "source" | "reference_sku" | "note"> | null = hubBomQuery.data
    ? {
        source: hubBomQuery.data.source,
        reference_sku: hubBomQuery.data.reference_sku,
        note: hubBomQuery.data.note,
      }
    : null;

  useEffect(() => {
    if (!laborGateReady || detailQuery.data?.assembly_type !== "serialized") return;
    const handle = window.setTimeout(() => pcbaSerialInputRef.current?.focus(), 0);
    return () => window.clearTimeout(handle);
  }, [laborGateReady, detailQuery.data?.assembly_type, buildOrderId]);

  const assembleMutation = useMutation({
    mutationFn: (input: { buildOrderId: number; pcbaSerial: string; notes?: string }) =>
      recordAssemblyUnit(input.buildOrderId, { pcbaSerial: input.pcbaSerial, notes: input.notes }),
    onSuccess: () => {
      setPcbaSerialDraft("");
      setSwappedVariableNames(new Set());
      void queryClient.invalidateQueries({ queryKey: ASSEMBLY_WORK_ORDERS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "assembly", "detail", buildOrderId] });
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "assembly", "units", buildOrderId] });
      showToast({ tone: "success", message: "Unit recorded as assembled." });
      window.setTimeout(() => pcbaSerialInputRef.current?.focus(), 0);
    },
    onError: (error: ApiError) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  useFloorPageScanCapture({
    enabled: writesAllowed && detailQuery.data?.assembly_type === "serialized",
    inputRef: pcbaSerialInputRef,
    onScan: (serial) => {
      setPcbaSerialDraft(serial);
      const notes =
        swappedVariableNames.size === 0
          ? undefined
          : `Component swaps: ${[...swappedVariableNames].sort().join(", ")}`;
      assembleMutation.mutate({ buildOrderId, pcbaSerial: serial, notes });
    },
  });

  const printMutation = useMutation({
    mutationFn: printAssemblyLabel,
    onSuccess: (result) => {
      showToast({
        tone: "success",
        message: `Label sent to printer: ${result.sku} × ${String(result.copies)}`,
      });
    },
    onError: (error: ApiError) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  function buildSwapNotes(): string | undefined {
    if (swappedVariableNames.size === 0) return undefined;
    const lines = [...swappedVariableNames].sort();
    return `Component swaps: ${lines.join(", ")}`;
  }

  function handleAssemble(): void {
    const serial = pcbaSerialDraft.trim();
    if (serial === "") return;
    assembleMutation.mutate({ buildOrderId, pcbaSerial: serial, notes: buildSwapNotes() });
  }

  function handlePrintLabel(): void {
    const sku = outputSku;
    if (sku === "") return;
    const copies = Number.parseInt(labelCopies, 10);
    printMutation.mutate({
      sku,
      printer,
      copies: Number.isFinite(copies) && copies > 0 ? copies : 1,
    });
  }

  function toggleSwapSlot(variableNames: readonly string[]): void {
    setSwappedVariableNames((current) => toggleAssemblySwapSlotVariables(current, variableNames));
  }

  if (!validId) {
    return <p className="text-sm text-rose-800">Invalid work order id.</p>;
  }

  if (!laborGateReady && laborPage !== "") {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 text-slate-600">
        <Loader2 aria-hidden className="h-5 w-5 animate-spin" />
        <p className="text-sm">Clocking in…</p>
      </div>
    );
  }

  const detail = detailQuery.data ?? null;

  if (detailQuery.isLoading) {
    return (
      <p className="text-sm text-slate-500">
        <Loader2 className="mr-1 inline h-4 w-4 animate-spin" aria-hidden /> Loading work order…
      </p>
    );
  }

  if (detailQuery.isError) {
    return (
      <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
        {(detailQuery.error as ApiError).message}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitleWithHelp
        hoverHint={WORK_PAGE_HELP}
        breadcrumbLabel={
          detail?.number?.trim()
          || (Number.isFinite(buildOrderId) ? `Work order ${String(buildOrderId)}` : "Work order")
        }
      >
        <span className="inline-flex flex-wrap items-center gap-2">
          <WorkOrderDetailLink buildOrderId={buildOrderId} workOrderNumber={detail?.number} />
        </span>
      </PageTitleWithHelp>

      {detail?.assembly_type === "build_order" ? (
        <AssemblyBuildOrderDetail detail={detail} />
      ) : detail?.assembly_type === "serialized" ? (
        <AssemblySerializedWorkSurface
          detail={detail}
          hubBomLines={hubBomQuery.data?.lines ?? []}
          hubBomMeta={hubBomMeta}
          hubBomLoading={hubBomQuery.isLoading}
          hubBomError={hubBomQuery.isError ? (hubBomQuery.error as ApiError).message : null}
          units={unitsQuery.data?.units ?? []}
          unitsLoading={unitsQuery.isLoading}
          pcbaSerialDraft={pcbaSerialDraft}
          onPcbaSerialDraftChange={setPcbaSerialDraft}
          pcbaSerialInputRef={pcbaSerialInputRef}
          swappedVariableNames={swappedVariableNames}
          onToggleSwapSlot={toggleSwapSlot}
          onAssemble={handleAssemble}
          assemblePending={assembleMutation.isPending}
          writesAllowed={writesAllowed}
        />
      ) : (
        <AssemblyLabelDetail
          detail={detail}
          hubBomLines={hubBomQuery.data?.lines ?? []}
          hubBomLoading={hubBomQuery.isLoading}
          hubBomError={hubBomQuery.isError ? (hubBomQuery.error as ApiError).message : null}
          printer={printer}
          station={station}
          onStationChange={setStation}
          unavailableMessage={unavailableMessage}
          labelCopies={labelCopies}
          onLabelCopiesChange={setLabelCopies}
          onPrint={handlePrintLabel}
          printPending={printMutation.isPending}
        />
      )}
    </div>
  );
}

function AssemblyBuildOrderDetail(props: { detail: AssemblyWorkOrderDetail }): JSX.Element {
  return (
    <div className="space-y-6">
      <dl className="grid max-w-xl grid-cols-2 gap-x-3 gap-y-2 text-sm">
        <dt className="text-slate-500">Finished good</dt>
        <dd className="font-mono text-slate-900">{props.detail.sku ?? "—"}</dd>
        <dt className="text-slate-500">Quantity</dt>
        <dd className="tabular-nums text-slate-900">{props.detail.quantity}</dd>
        <dt className="text-slate-500">Received</dt>
        <dd className="tabular-nums text-slate-900">{effectiveWorkOrderReceivedQuantity(props.detail)}</dd>
        <dt className="text-slate-500">Megaventory</dt>
        <dd className="text-slate-900">{props.detail.mv_number ?? "—"}</dd>
        <dt className="text-slate-500">Reference</dt>
        <dd className="text-slate-900">{props.detail.mfr_reference_number ?? "—"}</dd>
      </dl>

      <div>
        <h2 className="text-sm font-semibold text-slate-900">Work order BOM (Megaventory)</h2>
        {(props.detail.bom ?? []).length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">No BOM lines on this work order yet.</p>
        ) : (
          <ul className="mt-2 max-w-xl space-y-1 text-sm">
            {(props.detail.bom ?? []).map((line) => (
              <li key={line.id} className="flex justify-between gap-3 font-mono text-xs">
                <span>{line.componentSku}</span>
                <span className="tabular-nums text-slate-600">× {line.quantityRemaining}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AssemblySerializedWorkSurface(props: {
  detail: AssemblyWorkOrderDetail;
  hubBomLines: HubBomLine[];
  hubBomMeta: Pick<HubBomResponse, "source" | "reference_sku" | "note"> | null;
  hubBomLoading: boolean;
  hubBomError: string | null;
  units: Array<{ id: number; pcba_serial: string; source_tla_sku: string | null }>;
  unitsLoading: boolean;
  pcbaSerialDraft: string;
  onPcbaSerialDraftChange: (value: string) => void;
  pcbaSerialInputRef: RefObject<HTMLInputElement | null>;
  swappedVariableNames: ReadonlySet<string>;
  onToggleSwapSlot: (variableNames: readonly string[]) => void;
  onAssemble: () => void;
  assemblePending: boolean;
  writesAllowed: boolean;
}): JSX.Element {
  const assembledCount = props.detail.certified_count ?? 0;
  const instructionSteps = props.detail.instruction_steps ?? [];
  const instructionRevisionLabel = assemblyInstructionRevisionTitleSuffix(
    props.detail.instruction_revision,
    props.detail.instruction_approved_at,
  );

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        <Card className="h-full">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <CardTitle>Work instructions</CardTitle>
                {instructionRevisionLabel != null ? (
                  <span className="text-xs font-normal text-slate-500">{instructionRevisionLabel}</span>
                ) : null}
              </div>
              <AssemblyHubBomButton
                lines={props.hubBomLines}
                bomMeta={props.hubBomMeta}
                loading={props.hubBomLoading}
                errorMessage={props.hubBomError}
              />
            </div>
          </CardHeader>
          <CardBody className="space-y-3">
            {props.detail.instruction_summary ? (
              <p className="text-sm font-medium text-slate-700">{props.detail.instruction_summary}</p>
            ) : null}
            {instructionSteps.length > 0 ? (
              <AssemblyWorkInstructionStepsList steps={instructionSteps} />
            ) : (
              <p className="text-sm text-slate-500">
                No approved work instructions are published for this assembly kind yet. Ask manufacturing management to
                add them under Assembly work instructions.
              </p>
            )}
          </CardBody>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Work order</CardTitle>
            </CardHeader>
            <CardBody>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                <dt className="text-slate-500">Output</dt>
                <dd className="font-mono text-slate-900">{props.detail.sku ?? "—"}</dd>
                <dt className="text-slate-500">Assembled</dt>
                <dd className="tabular-nums text-slate-900">
                  {assembledCount} / {props.detail.quantity}
                </dd>
                <dt className="text-slate-500">Input SKUs</dt>
                <dd className="font-mono text-xs text-slate-900">
                  {(props.detail.source_skus ?? []).join(" · ") || "—"}
                </dd>
                <dt className="text-slate-500">Reference</dt>
                <dd className="text-slate-900">{props.detail.mfr_reference_number ?? "—"}</dd>
                {props.detail.material_plan != null ? (
                  <>
                    <dt className="text-slate-500">Material plan</dt>
                    <dd className="text-slate-900">
                      {props.detail.material_plan.plan_mode === "scratch"
                        ? "Scratch build"
                        : `Rebuild from ${props.detail.material_plan.source_ref ?? "source"}`}
                    </dd>
                  </>
                ) : null}
              </dl>
              {props.detail.material_plan != null ? (
                <p className="mt-3 text-xs text-slate-500">
                  Mid-job BOM changes use hub-native work order deviations on the manufacturing work orders page.
                </p>
              ) : null}
            </CardBody>
          </Card>

          <AssemblyComponentSwapSection
            lines={props.hubBomLines}
            swapSlots={props.detail.component_swap_slots}
            bomNote={props.hubBomMeta?.note}
            swappedVariableNames={props.swappedVariableNames}
            onToggleSlot={props.onToggleSwapSlot}
            disabled={!props.writesAllowed || props.assemblePending}
            loading={props.hubBomLoading}
            errorMessage={props.hubBomError}
          />

          {props.writesAllowed ? (
            <Card>
              <CardHeader>
                <CardTitle>Record assembly</CardTitle>
              </CardHeader>
              <CardBody>
                <Label htmlFor="assembly-pcba-serial">PCBA serial</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input
                    ref={props.pcbaSerialInputRef}
                    id="assembly-pcba-serial"
                    value={props.pcbaSerialDraft}
                    onChange={(event) => props.onPcbaSerialDraftChange(event.target.value)}
                    placeholder="46552-00001 or SD-8211-2126-00090"
                    autoComplete="off"
                    {...FLOOR_PAGE_SCAN_TARGET_PROPS}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        props.onAssemble();
                      }
                    }}
                  />
                  <Button
                    onClick={props.onAssemble}
                    disabled={props.assemblePending || props.pcbaSerialDraft.trim() === ""}
                    isLoading={props.assemblePending}
                  >
                    <CheckCircle2 className="h-4 w-4" aria-hidden />
                    Assemble
                  </Button>
                </div>
                <p className="mt-2 text-xs text-slate-600">
                  Scan the Kodiak PCBA serial after assembly steps are complete. Each serial can only be assembled once
                  on this work order.
                </p>
              </CardBody>
            </Card>
          ) : null}
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-slate-900">Assembled units</h2>
        {props.unitsLoading ? (
          <p className="mt-2 text-sm text-slate-500">Loading…</p>
        ) : props.units.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">No units assembled yet.</p>
        ) : (
          <ul className="mt-2 max-h-96 space-y-1 overflow-y-auto text-sm">
            {props.units.map((unit) => (
              <li
                key={unit.id}
                className="flex items-center justify-between gap-2 rounded border border-slate-100 px-2 py-1 font-mono text-xs"
              >
                <span>{unit.pcba_serial}</span>
                <span className="text-slate-500">{unit.source_tla_sku ?? "—"}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AssemblyLabelDetail(props: {
  detail: AssemblyWorkOrderDetail | null;
  hubBomLines: HubBomLine[];
  hubBomLoading: boolean;
  hubBomError: string | null;
  printer: string;
  station: string;
  onStationChange: (station: string) => void;
  unavailableMessage: string | null;
  labelCopies: string;
  onLabelCopiesChange: (value: string) => void;
  onPrint: () => void;
  printPending: boolean;
}): JSX.Element {
  return (
    <div className="space-y-6">
      <dl className="grid max-w-xl grid-cols-2 gap-x-3 gap-y-2 text-sm">
        <dt className="text-slate-500">Finished good</dt>
        <dd className="font-mono text-slate-900">{props.detail?.sku ?? "—"}</dd>
        <dt className="text-slate-500">Quantity</dt>
        <dd className="tabular-nums text-slate-900">{props.detail?.quantity ?? "—"}</dd>
        <dt className="text-slate-500">Megaventory</dt>
        <dd className="text-slate-900">{props.detail?.mv_number ?? "—"}</dd>
      </dl>

      <div>
        <h2 className="text-sm font-semibold text-slate-900">Work order BOM (Megaventory)</h2>
        {(props.detail?.bom ?? []).length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">No BOM lines on this work order yet.</p>
        ) : (
          <ul className="mt-2 max-w-xl space-y-1 text-sm">
            {(props.detail?.bom ?? []).map((line) => (
              <li key={line.id} className="flex justify-between gap-3 font-mono text-xs">
                <span>{line.componentSku}</span>
                <span className="tabular-nums text-slate-600">× {line.quantityRemaining}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h2 className="text-sm font-semibold text-slate-900">Hub BOM</h2>
        {props.hubBomLoading ? (
          <p className="mt-2 text-sm text-slate-500">Loading hub BOM…</p>
        ) : props.hubBomError != null ? (
          <p className="mt-2 text-sm text-amber-800">{props.hubBomError}</p>
        ) : (
          <ul className="mt-2 max-w-xl space-y-1 text-sm">
            {props.hubBomLines.map((line) => (
              <li key={line.variable_name} className="flex justify-between gap-3 font-mono text-xs">
                <span>{line.component_sku ?? "—"}</span>
                <span className="text-slate-500">{line.variable_name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="max-w-xl rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
        <h2 className="text-sm font-semibold text-slate-900">4x2 label</h2>
        <p className="mt-1 text-xs text-slate-600">
          Smart Grid Solutions logo, finished-good SKU, and data matrix.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <ZplLocationSelect
            id="assembly-label-location"
            label="Location"
            value={props.station}
            onChange={props.onStationChange}
            unavailableMessage={props.unavailableMessage}
            disabled={props.printPending}
          />
          <div>
            <Label htmlFor="assembly-label-copies">Copies</Label>
            <Input
              id="assembly-label-copies"
              type="number"
              min={1}
              max={99}
              className="mt-1.5"
              value={props.labelCopies}
              onChange={(event) => props.onLabelCopiesChange(event.target.value)}
              disabled={props.printPending}
            />
          </div>
        </div>
        <Button
          className="mt-3"
          size="sm"
          onClick={props.onPrint}
          disabled={props.printPending || props.printer.trim() === ""}
          isLoading={props.printPending}
        >
          <Printer className="h-4 w-4" aria-hidden />
          Print label
        </Button>
      </div>
    </div>
  );
}
